import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik, getIn } from "formik";
import { chain, isEmpty, isNil, isString } from "lodash-es";
import moment, { Moment } from "moment";
import { forwardRef, useMemo, useState } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { TextHelper } from "@/common/helpers/text";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useEntityInputFormInfo } from "@/common/hooks/entity/useEntityInputFormInfo";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserAffiliation } from "@/common/hooks/user/useUserAffiliation";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { enumService } from "@/common/services/enum";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  ContractStage,
  EntityType,
  VehicleArea,
  VehicleDto,
  VisualInspectionCreateDto,
  VisualInspectionDto,
  VisualInspectionItemDto,
  VisualInspectionItemInputDto,
  VisualInspectionUpdateDto,
} from "@/core/api/generated/v1";

import NoDataAlert from "../../AppAlerts/NoDataAlert";
import AppDropdownButton from "../../Button/AppDropdownButton";
import AppIconButton from "../../Button/AppIconButton";
import FoldableBlock from "../../Display/FoldableBlock";
import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FileUploader from "../../Files/FileUploader";
import FormActions from "../../Form/FormActions";
import AppTextArea from "../../Form/Input/AppTextArea";
import FuelLevelInput from "../../Form/Input/FuelLevelInput";
import MileageInput from "../../Form/Input/MileageInput";
import AppIcon from "../../Icons/AppIcon";
import AppTypography from "../../Text/AppTypography";
import ContractAutocompleteOrCreate from "../Contract/ContractAutocompleteOrCreate";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import InferEntityAffiliationFromVehicleFormikComputedField from "../EntityAffiliation/InferEntityAffiliationFromVehicleFormikComputedField";
import GeneralAttachedTagsInput from "../General/GeneralTag/GeneralAttachedTagsInput";
import VehicleAutocompleteOrCreate from "../Vehicle/VehicleAutocompleteOrCreate";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
  FormWrapperHandle,
} from "../components/BaseEntityCreateUpdate";
import { BaseEntityCreateUpdateExtras } from "../components/BaseEntityCreateUpdateExtras";

type DefaultValues = {
  vehicleId?: VisualInspectionCreateDto["vehicleId"];
  contractId?: VisualInspectionCreateDto["contractId"];
  departmentIds?: VisualInspectionCreateDto["departmentIds"];
  locationIds?: VisualInspectionCreateDto["locationIds"];
  tenantRequestsMeta?: VisualInspectionCreateDto["tenantRequestsMeta"];
  inspectedAt?: VisualInspectionCreateDto["inspectedAt"];
};

export type VisualInspectionCreateUpdateHandle = FormWrapperHandle;

export interface VisualInspectionCreateUpdateProps extends BaseEntityCreateUpdateInheritableProps<
  VisualInspectionDto,
  DefaultValues
> {
  visualInspectionId?: string;
}

export default forwardRef<VisualInspectionCreateUpdateHandle, VisualInspectionCreateUpdateProps>(
  function VisualInspectionCreateUpdate(
    {
      visualInspectionId,
      defaultValues,
      onCreate,
      onUpdate,
      onSave,
      onCancel,
    }: VisualInspectionCreateUpdateProps,
    ref,
  ) {
    const formInfo = useEntityInputFormInfo({ entityId: visualInspectionId });

    const mounted = useMounted();
    const { enqueueSnackbar } = useAppSnackbar();
    const profile = useUserProfile();
    const userAffiliation = useUserAffiliation();

    const isCreate = !visualInspectionId;
    // const isEdit = !!visualInspectionId;

    const [isAttachmentFilesUploading, setIsAttachmentFilesUploading] = useState(false);

    const visualInspectionRequest = useApiRequest(
      apiClient.visualInspectionsApi.apiV1VisualInspectionsVisualInspectionIdGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        visualInspectionId: visualInspectionId!,
      },
      {
        deps: [visualInspectionId],
        skip: !visualInspectionId,
      },
    );
    const visualInspection = visualInspectionRequest?.data;

    const createMetaRequest = useApiRequest(
      apiClient.visualInspectionsApi.apiV1VisualInspectionsCreateMetaGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      },
      {
        deps: [],
      },
    );
    const createMeta = createMetaRequest?.data;

    useBreadcrumbReplacements({
      waitTimeout: 10_000,
      idBreadcrumb: visualInspection && {
        idValue: visualInspection.id!,
        newTitle: visualInspection.localNumber || "",
      },
    });

    const vehicleIdComputed = defaultValues?.vehicleId || visualInspection?.vehicle?.id;
    const contractIdComputed = defaultValues?.contractId || visualInspection?.contract?.id;

    // on create we use settings from meta, but on update from the entity as settings might change over time
    const settingsTemp = isCreate ? createMeta?.settings : visualInspection?.settings;
    const settingsComputed = useMemo(
      () =>
        settingsTemp
          ? {
              ...settingsTemp,
              perAreaMap: chain(settingsTemp.perArea)
                .keyBy((x) => x.area!)
                .mapValues((x) => x)
                .valueOf(),
            }
          : undefined,
      [settingsTemp],
    );

    return (
      <BaseEntityCreateUpdate
        entityType={EntityType.VisualInspection}
        entityId={visualInspectionId}
        entity={visualInspection}
        entityRequest={visualInspectionRequest}
        isIniting={
          visualInspectionRequest.isLoading ||
          createMetaRequest.isLoading ||
          !createMeta ||
          userAffiliation.isIniting
        }
      >
        <Formik<
          BaseFormikValues &
            Omit<VisualInspectionCreateDto, "items"> &
            Omit<VisualInspectionUpdateDto, "items"> & {
              vehicle?: VehicleDto;
              initialAttachments: VisualInspectionDto["attachments"];
              isAttachmentFilesHaveErrors: boolean;
              items?: Array<
                VisualInspectionItemInputDto & {
                  isAttachmentFilesHaveErrors?: boolean;
                  initialAttachments?: VisualInspectionItemDto["attachments"];
                }
              >;
            }
        >
          enableReinitialize={false}
          initialValues={{
            departmentIds:
              (!isEmpty(defaultValues?.departmentIds) ? defaultValues!.departmentIds : undefined) ||
              (!isEmpty(visualInspection?.departmentIds)
                ? visualInspection!.departmentIds
                : undefined) ||
              (!isEmpty(userAffiliation.departments)
                ? userAffiliation.departments!.map((d) => d.id!)
                : undefined),
            locationIds:
              (!isEmpty(defaultValues?.locationIds) ? defaultValues!.locationIds : undefined) ||
              (!isEmpty(visualInspection?.locationIds)
                ? visualInspection!.locationIds
                : undefined) ||
              (!isEmpty(userAffiliation.locations)
                ? userAffiliation.locations!.map((l) => l.id!)
                : undefined),
            contractId: visualInspection?.contract?.id || defaultValues?.contractId || undefined,
            vehicleId: visualInspection?.vehicle?.id || defaultValues?.vehicleId || undefined,
            vehicle: undefined,
            externalNumber: visualInspection?.externalNumber || undefined,
            inspectedAt:
              visualInspection?.inspectedAt ||
              defaultValues?.inspectedAt ||
              moment().format(DATETIME_FORMATS.DISPLAY_DATETIME),
            inspector: {
              isCurrentUser: true,
              ...(visualInspection?.inspector || {}),
              phoneNumber: visualInspection?.inspector?.phoneNumber,
            },
            isUseSupplierBranding: visualInspection ? visualInspection.isUseSupplierBranding : true,
            mileage: visualInspection?.mileage || undefined,
            fuelLevel: visualInspection?.fuelLevel ?? undefined,
            notes: visualInspection?.notes || "",
            initialAttachments: visualInspection?.attachments || undefined,
            isAttachmentFilesHaveErrors: false,
            attachments: !isEmpty(visualInspection?.attachments)
              ? visualInspection?.attachments
              : undefined,
            items:
              visualInspection?.items?.map((x) => ({
                ...x,
                isAttachmentFilesHaveErrors: false,
                initialAttachments: x?.attachments || undefined,
                attachments: !isEmpty(x?.attachments) ? x.attachments : undefined,
              })) ||
              createMeta?.settings?.perArea
                ?.filter((x) => x.isRequired)
                ?.map((x) => ({
                  area: x.area!,
                }))
                ?.extensions.nullIfEmpty() ||
              [VehicleArea.Exterior, VehicleArea.Interior].map((x) => ({ area: x })),
            settings: visualInspection?.settings,
            tenantRequestsMeta:
              visualInspection?.tenantRequestsMeta ||
              defaultValues?.tenantRequestsMeta ||
              undefined,
            tags: visualInspection?.tags || undefined,
            submit: "",
          }}
          validationSchema={Yup.object().shape({
            // contractId: Yup.string().required("Contract is required"),
          })}
          onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
            const values2: typeof values = {
              ...values,
              contractId: contractIdComputed,
              inspectedAt: values.inspectedAt
                ? moment(values.inspectedAt).utc().format()
                : undefined,
              items: values.items?.filter((x) => ArrayHelper.isNotEmpty(x.attachments)),
            };

            try {
              if (isCreate) {
                const response = await apiClient.visualInspectionsApi.apiV1VisualInspectionsPost({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  visualInspectionCreateDto: { ...values2 },
                });
                enqueueSnackbar("Visual inspection created.", { variant: "success" });
                onCreate && (await onCreate(response.data));
                onSave && (await onSave(response.data));
              } else {
                const response =
                  await apiClient.visualInspectionsApi.apiV1VisualInspectionsVisualInspectionIdPut({
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    visualInspectionId,
                    visualInspectionUpdateDto: {
                      ...values2,
                    },
                  });
                enqueueSnackbar("Visual inspection updated.", { variant: "success" });
                onUpdate && (await onUpdate(response.data));
                onSave && (await onSave(response.data));
              }

              if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err: any) {
              if (mounted.current) {
                ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                setStatus({ success: false });
                setSubmitting(false);
              }
            }
          }}
        >
          {(formikProps) => {
            const {
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
            } = formikProps;

            const areasToSelect = enumService.getEnumValues("VehicleArea", {
              exceptNone: true,
              except: values.items?.map((x) => x.area || VehicleArea.None),
            });

            return (
              <form noValidate onSubmit={handleSubmit}>
                <BaseEntityCreateUpdateExtras
                  defaultImperativeHandle={{
                    formWrapperRef: ref,
                    formikProps: formikProps,
                  }}
                  guardProps={{
                    formikProps: formikProps,
                  }}
                />

                <InferEntityAffiliationFromVehicleFormikComputedField
                  formInfo={formInfo}
                  values={{
                    vehicleId: values.vehicleId,
                    vehicle: values.vehicle,
                    departmentIds: values.departmentIds,
                    locationIds: values.locationIds,
                  }}
                  onComputed={(computed) => {
                    setFieldValue("vehicle", computed.vehicle);
                    setFieldValue("departmentIds", computed.departmentIds);
                    setFieldValue("locationIds", computed.locationIds);
                  }}
                />

                <FormContentBlock gap={1}>
                  {/* Tags */}
                  <FormControl margin='dense' fullWidth>
                    <GeneralAttachedTagsInput
                      value={values.tags}
                      onChange={(newValue) => {
                        setFieldValue("tags", newValue);
                      }}
                    />
                    <FormHelperText error>
                      {ValidationHelper.getFormikErrorsAsString(errors.tags, {
                        isIncludeNested: false,
                      })}
                    </FormHelperText>
                  </FormControl>

                  {!isCreate && (
                    <TextField
                      fullWidth
                      disabled
                      label='Inspection number'
                      margin='dense'
                      type='text'
                      value={visualInspection?.localNumber}
                      variant='outlined'
                    />
                  )}

                  <FormControl margin='dense' fullWidth error={Boolean(errors.vehicleId)}>
                    <VehicleAutocompleteOrCreate
                      autocompleteProps={{
                        required: true,
                        disabled: !isCreate || !isEmpty(vehicleIdComputed),
                        entityId: values.vehicleId,
                        isPreload: isCreate,
                        onChange: (newValue) => {
                          setFieldValue(`vehicleId`, newValue?.id);
                          setFieldValue(`vehicle`, newValue);
                          setFieldValue(
                            "departmentIds",
                            values.vehicleId !== newValue?.id
                              ? undefined
                              : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                          );
                          setFieldValue(
                            "locationIds",
                            values.vehicleId !== newValue?.id
                              ? undefined
                              : ArrayHelper.undefinedIfEmpty(values.locationIds),
                          );
                        },
                      }}
                      createFormPlacement='modal'
                      onCreate={(newValue) => {
                        setFieldValue(`vehicleId`, newValue?.id);
                        setFieldValue(`vehicle`, newValue);
                        setFieldValue(
                          "departmentIds",
                          values.vehicleId !== newValue?.id
                            ? undefined
                            : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                        );
                        setFieldValue(
                          "locationIds",
                          values.vehicleId !== newValue?.id
                            ? undefined
                            : ArrayHelper.undefinedIfEmpty(values.locationIds),
                        );
                      }}
                    />
                    <FormHelperText>{errors.vehicleId}</FormHelperText>
                  </FormControl>

                  {/* Entity affiliation */}
                  <EntityAffiliationInputV2
                    departments={{
                      departmentIds: values.departmentIds,
                      onChange: (departmentIds) => {
                        setFieldValue("departmentIds", departmentIds);
                      },
                      error: errors.departmentIds,
                    }}
                    locations={{
                      locationIds: values.locationIds,
                      onChange: (locationIds) => {
                        setFieldValue("locationIds", locationIds);
                      },
                      error: errors.locationIds,
                    }}
                  />

                  {values.contractId && (
                    <FormControl margin='dense' fullWidth error={Boolean(errors.contractId)}>
                      <ContractAutocompleteOrCreate
                        autocompleteProps={{
                          disabled: true,
                          entityId: values.contractId,
                          isPreload: isCreate,
                          searchFilters: {
                            vehicleId: values.vehicleId || undefined,
                            excludeStage: ContractStage.Draft,
                          },
                          onChange: (newValue) => {
                            setFieldValue(`contractId`, newValue?.id);
                            setFieldValue(`contract`, newValue || undefined);
                          },
                        }}
                        createFormPlacement='modal'
                        onCreate={(newValue) => {
                          setFieldValue(`contractId`, newValue?.id);
                          setFieldValue(`contract`, newValue || undefined);
                        }}
                      />
                      <FormHelperText>{errors.contractId}</FormHelperText>
                    </FormControl>
                  )}

                  <TextField
                    fullWidth
                    error={Boolean(touched.externalNumber && errors.externalNumber)}
                    helperText={touched.externalNumber && errors.externalNumber}
                    label='External number'
                    margin='dense'
                    name='externalNumber'
                    onChange={(newValue) => {
                      setFieldValue("externalNumber", newValue.target.value);
                    }}
                    type='text'
                    value={values?.externalNumber || ""}
                    variant='outlined'
                  />

                  <FormControl
                    margin='dense'
                    fullWidth
                    error={Boolean(touched.inspectedAt && errors.inspectedAt)}
                  >
                    <AppDateTimePicker
                      label='Inspected at'
                      value={(values.inspectedAt && moment(values.inspectedAt)) || null}
                      format={DATETIME_FORMATS.DISPLAY_DATETIME}
                      onChange={(newValue: Moment | null) => {
                        setFieldValue("inspectedAt", newValue?.format() || null);
                      }}
                    />
                    <FormHelperText>{touched.inspectedAt && errors.inspectedAt}</FormHelperText>
                  </FormControl>

                  {/* Mileage */}
                  <MileageInput
                    error={Boolean(touched.mileage && errors.mileage)}
                    helperText={touched.mileage && errors.mileage}
                    vehicle={values.vehicle}
                    disabled={!values.vehicle || !isCreate}
                    fullWidth
                    name='mileage'
                    onBlur={handleBlur}
                    onChange={(newValue) => {
                      setFieldValue("mileage", newValue);
                    }}
                    value={values.mileage}
                  />

                  <FormControl
                    margin='dense'
                    fullWidth
                    error={Boolean(touched.fuelLevel && errors.fuelLevel)}
                  >
                    <Typography component='div' gutterBottom>
                      Fuel level
                    </Typography>

                    <FuelLevelInput
                      onBlur={handleBlur}
                      onChange={(e, val) => setFieldValue("fuelLevel", val ?? undefined)}
                      value={values.fuelLevel ?? undefined}
                    />
                    <FormHelperText>{touched.fuelLevel && errors.fuelLevel}</FormHelperText>
                  </FormControl>
                  <FormControl margin='dense' fullWidth>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isUseSupplierBranding}
                          onChange={(e) => setFieldValue(`isUseSupplierBranding`, e.target.checked)}
                        />
                      }
                      label={
                        <Typography component='span' sx={{ display: "flex" }}>
                          Include supplier branding
                        </Typography>
                      }
                    />
                  </FormControl>

                  {/* Items */}
                  <FoldableBlock
                    defaultIsFolded={false}
                    trigger={{
                      label: (
                        <Typography component='span' variant='h6'>
                          Inspections *
                        </Typography>
                      ),
                    }}
                  >
                    <Stack spacing={2}>
                      {(!values.items || values.items.length === 0) && (
                        <NoDataAlert title='No items yet' />
                      )}

                      <Stack direction='column' spacing={1}>
                        {values.items?.map((item, index) => {
                          return (
                            <FormContentBlock key={index}>
                              <Stack spacing={2}>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 0fr",
                                    columnGap: 2,
                                  }}
                                >
                                  <Typography component='span' variant='subtitle1'>
                                    <InlineApiEnumValue type='VehicleArea' value={item.area} />
                                  </Typography>

                                  <Stack direction='row'>
                                    <AppIconButton
                                      onClick={() =>
                                        setFieldValue(
                                          "items",
                                          ArrayHelper.removeByIndex(values.items, index),
                                        )
                                      }
                                    >
                                      <AppIcon of='delete' />
                                    </AppIconButton>
                                  </Stack>
                                </Box>

                                <Box>
                                  <FormControl fullWidth margin='dense'>
                                    <FileUploader
                                      multiple
                                      maxFiles={30}
                                      defaultFiles={
                                        values.items &&
                                        FileItem.createManyFrom(
                                          values.items[index].initialAttachments,
                                        )
                                      }
                                      onChange={(newFiles) => {
                                        setFieldValue(
                                          `items[${index}].attachments`,
                                          FileItem.toManyGeneralAttachmentInputDto(newFiles),
                                        );
                                      }}
                                      onUploadStarted={() => {
                                        setIsAttachmentFilesUploading(true);
                                      }}
                                      onUploadFinished={() => {
                                        setIsAttachmentFilesUploading(false);
                                      }}
                                      onValidationStatusChange={(filesValidationStatus) => {
                                        if (filesValidationStatus)
                                          setFieldValue(
                                            `items[${index}].isAttachmentFilesHaveErrors`,
                                            Object.values(filesValidationStatus).some(
                                              (x) => x === false,
                                            ),
                                          );
                                      }}
                                    />

                                    {item.area &&
                                      settingsComputed?.perAreaMap &&
                                      settingsComputed.perAreaMap[item.area] && (
                                        <FormHelperText>
                                          {!isNil(
                                            settingsComputed.perAreaMap[item.area].minPhotoCount,
                                          ) && (
                                            <Box>
                                              Min{" "}
                                              {settingsComputed.perAreaMap[item.area].minPhotoCount}{" "}
                                              {TextHelper.pluralizeManual(
                                                "photo",
                                                settingsComputed.perAreaMap[item.area]
                                                  .minPhotoCount || 0,
                                                "photos",
                                              )}{" "}
                                              *
                                            </Box>
                                          )}

                                          {!isNil(
                                            settingsComputed.perAreaMap[item.area]
                                              .minAttachmentCount,
                                          ) && (
                                            <Box>
                                              Min{" "}
                                              {
                                                settingsComputed.perAreaMap[item.area]
                                                  .minAttachmentCount
                                              }{" "}
                                              {TextHelper.pluralizeManual(
                                                "attachment",
                                                settingsComputed.perAreaMap[item.area]
                                                  .minAttachmentCount || 0,
                                                "attachments",
                                              )}{" "}
                                              *
                                            </Box>
                                          )}
                                        </FormHelperText>
                                      )}

                                    <FormHelperText error>
                                      {getIn(errors, `items[${index}].attachments`)}
                                    </FormHelperText>
                                  </FormControl>
                                </Box>
                              </Stack>
                            </FormContentBlock>
                          );
                        })}
                      </Stack>

                      {errors.items && isString(errors.items) && (
                        <FormHelperText error>{errors.items}</FormHelperText>
                      )}

                      {/* Add item */}
                      <Box>
                        <AppDropdownButton
                          disabled={isEmpty(areasToSelect)}
                          buttonProps={{
                            color: "text",
                            size: "small",
                            variant: "outlined",
                            startIcon: <AppIcon of='add' />,
                          }}
                          dropdownContent={
                            <MenuList>
                              {areasToSelect.map((area) => (
                                <MenuItem
                                  key={area}
                                  onClick={() => {
                                    setFieldValue("items", [
                                      ...(values.items || []),
                                      {
                                        area: area,
                                      },
                                    ]);
                                  }}
                                >
                                  <ListItemIcon>
                                    <AppIcon of='add' fontSize='small' />
                                  </ListItemIcon>
                                  <ListItemText>
                                    <InlineApiEnumValue type='VehicleArea' value={area} />
                                  </ListItemText>
                                </MenuItem>
                              ))}
                            </MenuList>
                          }
                        >
                          Add item
                        </AppDropdownButton>
                      </Box>
                    </Stack>
                  </FoldableBlock>

                  {/* Other */}
                  <FoldableBlock
                    defaultIsFolded
                    trigger={{
                      label: (
                        <Typography component='span' variant='h6'>
                          Other
                        </Typography>
                      ),
                    }}
                  >
                    <AppTextArea
                      error={Boolean(touched.notes && errors.notes)}
                      fullWidth
                      helperText={touched.notes && errors.notes}
                      mode='notes'
                      margin='dense'
                      name='notes'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.notes}
                      variant='outlined'
                    />

                    <FormControl fullWidth component='fieldset' margin='dense'>
                      <FileUploader
                        multiple
                        maxFiles={30}
                        defaultFiles={FileItem.createManyFrom(values.initialAttachments)}
                        onChange={(newFiles) => {
                          setFieldValue(
                            `attachments`,
                            FileItem.toManyGeneralAttachmentInputDto(newFiles),
                          );
                        }}
                        onUploadStarted={() => {
                          setIsAttachmentFilesUploading(true);
                        }}
                        onUploadFinished={() => {
                          setIsAttachmentFilesUploading(false);
                        }}
                        onValidationStatusChange={(filesValidationStatus) => {
                          if (filesValidationStatus)
                            setFieldValue(
                              "isAttachmentFilesHaveErrors",
                              Object.values(filesValidationStatus).some((x) => x === false),
                            );
                        }}
                      />
                    </FormControl>
                  </FoldableBlock>

                  <Box>
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Switch
                            disabled
                            checked={values.inspector?.isCurrentUser}
                            onChange={handleChange}
                            name='inspector.isCurrentUser'
                          />
                        }
                        label={
                          <AppTypography
                            decoration={{ variant: "helpText" }}
                            tooltipProps={{
                              title: "Currently logged in user evaluated the damages",
                            }}
                          >
                            Inspected by{" "}
                            {values.inspector?.personName
                              ? `${values.inspector.personName?.firstName} ${values.inspector.personName?.lastName}`
                              : profile?.personName?.name}
                          </AppTypography>
                        }
                      />
                    </FormControl>
                  </Box>
                </FormContentBlock>

                {errors.settings && isString(errors.settings) && (
                  <FormHelperText error>{errors.settings}</FormHelperText>
                )}

                <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                <FormActions>
                  <Button
                    color='primary'
                    disabled={
                      isAttachmentFilesUploading ||
                      values.isAttachmentFilesHaveErrors ||
                      values.items?.some((x) => x.isAttachmentFilesHaveErrors === true)
                    }
                    loading={isSubmitting}
                    type='submit'
                    variant='contained'
                  >
                    Save
                  </Button>

                  {onCancel && (
                    <Button
                      variant='outlined'
                      color='text'
                      disabled={isSubmitting}
                      onClick={onCancel}
                    >
                      Cancel
                    </Button>
                  )}
                </FormActions>
              </form>
            );
          }}
        </Formik>
      </BaseEntityCreateUpdate>
    );
  },
);
